/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.com/docs/use-static-query/
 */

import * as React from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"

function Seo({ description, lang, meta, title, imageURL }) {
  const { site, strapiHomepage } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
          }
        }
        strapiHomepage {
          mainImage {
            localFile {
              publicURL
            }
          }
          seo {
            shareImage {
              localFile {
                publicURL
              }
            }
          }
        }
      }
    `
  )

  const metaDescription = description || site.siteMetadata.description
  const defaultTitle = site.siteMetadata?.title
  const rootUrl = 'https://www.omnicrane.com'

  const getAbsoluteUrl = (url) => {
    if (!url) return null
    if (url.startsWith('http')) return url
    if (url.startsWith('/')) return `${rootUrl}${url}`
    return `${rootUrl}/${url}`
  }

  const image = getAbsoluteUrl(
    imageURL ||
    (strapiHomepage.seo.shareImage?.localFile?.publicURL) ||
    (strapiHomepage.mainImage?.localFile?.publicURL)
  )

  const isCzech = lang === 'cs-CZ'

  const defaultTitleText = isCzech 
    ? "B2B prodejní a produktová AI aplikace | OmniCrane"
    : "B2B Sales & Product AI App | OmniCrane"

  const defaultDescription = isCzech
    ? "OmniCrane poskytuje AI řešení pro B2B obchodníky a produktové manažery. Přetvořte svá produktová data na digitální dvojče a získejte jasné odpovědi na to, co, kdy a jak prodávat."
    : "OmniCrane provides AI-powered solutions for B2B sales and product professionals. Transform your product data into a digital twin and get clear answers on what, when, and how to sell."

  const defaultOgDescription = isCzech
    ? "AI platforma pro B2B obchodní a produktové týmy. Přeměňte produktová data na digitální dvojče a získejte akční obchodní doporučení."
    : "AI platform for B2B sales and product teams. Turn your product data into a digital twin and get actionable sales insights."

  const defaultUrl = isCzech
    ? `${rootUrl}/cs-CZ`
    : rootUrl

  const defaultImage = `${rootUrl}/static/72a852f479f85868e9968e30bac3726a/2b8a7/Dasboard_Done_No_BG_f64083d366.webp`

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title || defaultTitleText}
      titleTemplate={defaultTitle ? `%s | ${defaultTitle}` : null}
      link={[
        {
          rel: "canonical",
          href: defaultUrl,
        },
        {
          rel: "alternate",
          href: rootUrl,
          hreflang: "en",
        },
        {
          rel: "alternate",
          href: `${rootUrl}/cs-CZ`,
          hreflang: "cs-CZ",
        },
        {
          rel: "alternate",
          href: rootUrl,
          hreflang: "x-default",
        },
      ]}
      meta={[
        {
          name: `description`,
          content: metaDescription || defaultDescription,
        },
        {
          name: `robots`,
          content: `index, follow`,
        },
        {
          name: `viewport`,
          content: `width=device-width, initial-scale=1.0`,
        },
        {
          property: `og:title`,
          content: title || defaultTitleText,
        },
        {
          property: `og:description`,
          content: metaDescription || defaultOgDescription,
        },
        {
          property: `og:image`,
          content: image || defaultImage,
        },
        {
          property: `og:url`,
          content: defaultUrl,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          property: `og:site_name`,
          content: `OmniCrane`,
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:creator`,
          content: site.siteMetadata?.author || ``,
        },
        {
          name: `twitter:title`,
          content: title || defaultTitleText,
        },
        {
          name: `twitter:description`,
          content: metaDescription || defaultDescription,
        },
      ].concat(meta)}
    />
  )
}

Seo.defaultProps = {
  lang: `en`,
  meta: [],
  description: ``,
}

Seo.propTypes = {
  description: PropTypes.string || PropTypes.JSX,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  imageURL: PropTypes.string,
}

export default Seo
